<template>
    <div class="floating-buttons-container">
        <!-- Main buttons in the required order -->
        <div class="main-buttons">
            <!-- Up and down buttons -->
            <button class="floating-btn" :disabled="!currentSaleLines.length" @click="$emit('move-selection-up')" title="Move Selection Up">
                <font-awesome-icon icon="fa-solid fa-arrow-up" />
            </button>
            <button class="floating-btn" :disabled="!currentSaleLines.length" @click="$emit('move-selection-down')" title="Move Selection Down">
                <font-awesome-icon icon="fa-solid fa-arrow-down" />
            </button>

            <!-- Quantity buttons -->
            <button class="floating-btn" :disabled="!selectedSaleLine" @click="$emit('add-qty-selected-line')" title="Add Quantity">
                <font-awesome-icon icon="fa-solid fa-plus" />
            </button>
            <button class="floating-btn" :disabled="!selectedSaleLine" @click="$emit('reduce-qty-selected-line')" title="Reduce Quantity">
                <font-awesome-icon icon="fa-solid fa-minus" />
            </button>

            <!-- Void, discount, and set quantity buttons -->
            <button class="floating-btn" :disabled="!selectedSaleLine" @click="$emit('void-selected-line')" title="Void Selected Item">
                <font-awesome-icon icon="fa-solid fa-trash" />
            </button>
            <button class="floating-btn" :disabled="!selectedSaleLine" @click="$emit('apply-discount-selected-line')" title="Apply Discount">
                <font-awesome-icon icon="fa-solid fa-percent" />
            </button>
            <button class="floating-btn" :disabled="!selectedSaleLine" @click="$emit('set-quantity')" title="Set Quantity">
                <font-awesome-icon icon="fa-solid fa-times" />
            </button>
            <button class="floating-btn" v-if="canUseScale" :disabled="!selectedSaleLine" @click="$emit('read-from-scale')" title="Read from Scale">
                <font-awesome-icon icon="fa-solid fa-weight-hanging" />
            </button>
        </div>
        
        <!-- Offline Create Customer Modal -->
        <custom-modal ref="offlineCustomerModal" title="Create Customer (Offline)" :is-form="true" @submit="submitOfflineCustomer">
            <div class="form-floating mb-2">
                <input type="text" class="form-control" id="offline_name" v-model="offlineCustomerForm.Name" placeholder="Customer Name" required>
                <label for="offline_name">Customer Name</label>
            </div>
            <div class="form-floating mb-2">
                <input type="tel" class="form-control" id="offline_contact" v-model="offlineCustomerForm.CellNumber" placeholder="Contact No." required>
                <label for="offline_contact">Contact No.</label>
            </div>
            <div class="form-floating mb-2">
                <input type="text" class="form-control" id="offline_surname" v-model="offlineCustomerForm.Surname" placeholder="Surname (optional)">
                <label for="offline_surname">Surname (optional)</label>
            </div>
            <div class="form-floating mb-2">
                <input type="text" class="form-control" id="offline_address" v-model="offlineCustomerForm.Adress1" placeholder="Address (optional)">
                <label for="offline_address">Address (optional)</label>
            </div>
        </custom-modal>
        
        <!-- Speed dial for other buttons -->
        <div class="speed-dial">
            <button class="floating-btn speed-dial-trigger" @click="toggleSpeedDial" title="More Options">
                <font-awesome-icon :icon="['fas', speedDialOpen ? 'chevron-right' : 'chevron-left']" />
            </button>

            <div class="speed-dial-buttons" v-if="speedDialOpen">
                <button class="floating-btn" @click="refreshPage" title="Refresh">
                    <font-awesome-icon icon="fa-solid fa-sync-alt" />
                </button>
                <button class="floating-btn" @click="toggleTheme" title="Toggle Theme">
                    <font-awesome-icon :icon="['fas', currentTheme === 'dark' ? 'sun' : 'moon']" />
                </button>
                <!-- Loyalty heart button: placed next to Add Customer so cashiers
                     can attach a loyalty member in the same speed-dial flow. -->
                <button class="floating-btn" v-if="showLoyalty"
                        :class="{ 'floating-btn--active': !!loyaltyDetails.barcode }"
                        @click="checkLoyalty"
                        title="Add Loyalty Customer">
                    <font-awesome-icon icon="heart-circle-check" v-if="!!loyaltyDetails.barcode" />
                    <font-awesome-icon icon="heart-circle-plus" v-else />
                </button>
                <button class="floating-btn" :class="{ 'disabled': isDisabled }" :disabled="isDisabled"
                    @click="$emit('unselect-customer')" v-if="routeName !== `viewSale` && customer" title="Remove Customer">
                    <font-awesome-icon icon="fa-solid fa-user-minus" />
                </button>
                <button class="floating-btn" :class="{ 'disabled': isDisabled }" :disabled="isDisabled"
                    @click="handleAddCustomer" v-if="routeName !== `viewSale` && !customer" title="Add Customer">
                    <font-awesome-icon icon="fa-solid fa-user-plus" />
                </button>
            </div>
        </div>
        <!-- Loyalty Scan Modal: opens when cashiers tap the heart icon above.
             Mirrors lytsoft NavigationBar.vue pattern: scan barcode, dispatch
             checkLoyaltyBarcode, then display loyalty customer details. -->
        <custom-modal title="Add Loyalty Customer To Sale" ref="scanLoyalty" @open="focusInput">
            <div v-if="!loyaltyDetails.barcode">
                <label for="loyalty-barcode">Scan Barcode</label>
                <input type="text" class="form-control" :disabled="$store.getters.getLoadingState" id="loyalty-barcode"
                    v-model="loyaltyBarcode" ref="loyaltyCode" @keyup.enter="validateBarcode">
            </div>
            <div class="d-flex flex-column" v-else>
                <span class="text-sm text-secondary">Barcode : {{ loyaltyDetails.barcode }}</span>
                <div class="d-flex flex-row">
                    Customer : {{ loyaltyDetails.name }} {{ loyaltyDetails.surname }}
                    <a class="btn btn-sm btn-outline-primary mx-2" @click="$store.commit(`setLoyaltyDetails`, {})">
                        <font-awesome-icon icon="close" />
                    </a>
                </div>
                <span>Contact : {{ loyaltyDetails.contact }}</span>
                Points Value : {{ loyaltyDetails.balance }} points - ({{ $utils.formatMoney(loyaltyDetails.pointsInCash)
                }})
            </div>
        </custom-modal>
    </div>

</template>

<script>
import Notification from "@/components/layout/navigation/Notification";
import CustomModal from "@/components/layout/utils/CustomModal";
import connectivityMonitor from '@/services/connectivityMonitor';
import { responses } from '@/utils/responses';
import { errorHandler } from '@/services/errorHandler';

export default {
    name: "FloatingButtons",
    components: { CustomModal, Notification },
    props: {
        isDisabled: { type: Boolean, default: false },
        customer: { type: Object, default: null },
        zedMobileStatus: { type: Boolean, default: false },
        routeName: { type: String, default: '' },
        selectedSaleLine: { type: Object, default: null },
        currentSaleLines: { type: Array, default: () => [] }, // New prop for up/down navigation
    },
    data() {
        return {
            loyaltyBarcode: null,
            speedDialOpen: false, // For speed dial functionality
            isOnline: true,
            unsubscribeOnline: null,
            unsubscribeOffline: null,
            unsubscribeOffline: null,
            offlineCustomerForm: { Name: '', CellNumber: '', Surname: '', Adress1: '', Area: '', vat: '' },
            canUseScale: false,
        };
    },
    computed: {
        salesScreens() {
            return [`pos`, `payment`, `split-payment`, `instruction`];
        },
        showLoyalty() {
            let isSalesScreen = this.salesScreens.includes(this.$route.name);
            return this.$store.getters.getHasLoyalty && isSalesScreen;
        },
        loyaltyDetails() {
            return this.$store.getters.getLoyaltyDetails;
        },
        currentTheme: {
            get() {
                return this.$store.getters.getCurrentTheme || (document.documentElement.getAttribute('data-theme') || 'light');
            },
            set(value) {
                this.$store.commit('setTheme', value);
            }
        }
    },

    mounted() {
        // Initialize theme via Vuex (mobile-safe)
        try {
            const savedTheme = localStorage.getItem('theme');
            if (savedTheme === 'light' || savedTheme === 'dark') {
                this.$store.commit('setTheme', savedTheme);
            } else if (window.matchMedia) {
                const mq = window.matchMedia('(prefers-color-scheme: dark)');
                this.$store.commit('setTheme', mq.matches ? 'dark' : 'light');
            }
        } catch (_) {
            this.$store.commit('setTheme', 'light');
        }
        
        // Initialize online status
        this.isOnline = connectivityMonitor.getStatus();
        
        // Subscribe to connectivity events
        this.unsubscribeOnline = connectivityMonitor.subscribe('online', () => {
            this.isOnline = true;
        });
        
        this.unsubscribeOffline = connectivityMonitor.subscribe('offline', () => {
            this.isOnline = false;
        });
    },
    
    beforeDestroy() {
        // Unsubscribe from connectivity events
        if (this.unsubscribeOnline) {
            this.unsubscribeOnline();
        }
        if (this.unsubscribeOffline) {
            this.unsubscribeOffline();
        }
    },

    methods: {
        toggleSpeedDial() {
            this.speedDialOpen = !this.speedDialOpen;
        },
        syncData() {
            return window.location.reload(true);
        },
        tawkTo() {
            return window.open('https://tawk.to/LytSupport', '_self');
        },
        validateBarcode() {
            if (!isValidString(this.loyaltyBarcode)) return;
            this.$refs.scanLoyalty.closeModal();
            this.$utils.showLoading();
            return this.$store
                .dispatch(`checkLoyaltyBarcode`, this.loyaltyBarcode)
                .finally(() => {
                    this.loyaltyBarcode = null;
                    this.$refs.scanLoyalty.openModal();
                });
        },
        checkLoyalty() {
            return this.$refs.scanLoyalty.openModal();
        },
        async focusInput() {
            if (!!this.loyaltyDetails.barcode) return;
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return this.$refs.loyaltyCode.focus();
        },
        refreshPage() {
            // Implement refresh logic, e.g., reload current route or dispatch an action
            this.$router.go(0); // Reloads the current route
        },
        toggleTheme() {
            const current = this.$store.getters.getCurrentTheme || (document.documentElement.getAttribute('data-theme') || 'light');
            const newTheme = current === 'light' ? 'dark' : 'light';
            this.$store.commit('setTheme', newTheme);
        },
        goToLoyalty() {
            this.$router.push({ name: 'loyalty-customers' });
        },
        async handleAddCustomer() {
            // If online, keep existing behavior: open select-customer flow in parent
            if (this.isOnline) {
                this.$emit('select-customer');
                return;
            }
            // Offline: open inline modal to create a quick customer and attach to sale
            if (this.$refs.offlineCustomerModal) {
                this.offlineCustomerForm = { Name: '', CellNumber: '', Surname: '', Adress1: '', Area: '', vat: '' };
                this.$refs.offlineCustomerModal.openModal();
            }
        },
        async submitOfflineCustomer() {
            try {
                if (!this.offlineCustomerForm?.Name || !this.offlineCustomerForm?.CellNumber) {
                    responses.showWarning('Please provide Name and Contact');
                    return;
                }
                this.$utils.showLoading();
                const rec = await this.$store.dispatch('createOfflineDebtor', this.offlineCustomerForm);
                if (rec && rec.Id) {
                    // set as current sale customer
                    this.$store.dispatch('setCustomer', rec.Id);
                    responses.showSuccess('Offline customer added to sale');
                    if (this.$refs.offlineCustomerModal) this.$refs.offlineCustomerModal.closeModal();
                }
            } catch (e) {
                errorHandler.tomcatError(e, false, 'Failed to save offline customer');
            } finally {
                this.$utils.hideLoading();
            }
        },
        async checkScaleAvailability() {
            this.canUseScale = false;
            // Guard: No item selected
             if (!this.selectedSaleLine) return;
             
            try {
                // 1. Check Local Setting
                const localSettings = localStorage.getItem('local_scale_settings');
                let useScale = false;
                if (localSettings) {
                    const config = JSON.parse(localSettings);
                    useScale = !!config.UseScale;
                }
                
                if (!useScale) return;

                // 2. Check Item Stock Setting
                const set = await this.$store.dispatch('getStockSetting', this.selectedSaleLine.Plu);
                if (set && (set.Scale || set.scale)) {
                    this.canUseScale = true;
                }
            } catch (e) {
                console.error('Error checking scale availability:', e);
            }
        }
    },
    watch: {
        selectedSaleLine: {
            handler(val) {
                if (val) this.checkScaleAvailability();
                else this.canUseScale = false;
            },
            immediate: true
        }
    }
};
</script>

<style lang="scss" scoped>
.floating-buttons-container {
    position: absolute;
    bottom: 10%;
    right: 10px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    z-index: 500;
}

.main-buttons {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 10px;
}

.speed-dial {
    position: relative;
}

.speed-dial-buttons {
    position: absolute;
    bottom: 0;
    right: 50px;
    display: flex;
    flex-direction: row;
    gap: 6px;
    background: var(--card-bg);
    border-radius: 10px;
    padding: 10px;
    box-shadow: var(--card-shadow);
    animation: slideInLeft 0.2s ease-in-out;
}

@keyframes slideInLeft {
    from {
        opacity: 0;
        transform: translateX(10px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

.floating-btn {
    background: var(--primary-button-bg);
    color: var(--primary-button-text);
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: var(--card-shadow);
    cursor: pointer;
    transition: transform 0.2s ease-in-out, background 0.2s ease-in-out;
    font-size: 0.9rem;

    &:hover {
        transform: scale(1.1);
        background: var(--primary-button-hover-bg);
    }

    &:active {
        transform: scale(0.95);
    }

    &.speed-dial-trigger {
        background: var(--accent-electric-blue);
    }
}

.disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Loyalty heart button: solid accent color when a loyalty member is
   attached so the cashier can see at a glance that the sale is linked
   to a loyalty identity. */
.floating-btn--active {
    background: var(--accent-electric-blue);
    color: #ffffff;

    &:hover {
        background: var(--accent-electric-blue);
        filter: brightness(1.1);
    }
}
</style>
